import {
  ACTIVE_STATUSES,
  competitionsCsvFilename,
  CSV_MAX_ROWS,
  DEFAULT_COMPETITION_SORT,
  DEFAULT_SORT_ORDER,
  type CompetitionRow,
  type CompetitionSort,
  type CompetitionStatusCount,
  type CompetitionsList,
  type CompetitionsQuery,
} from '@/api/competitions'
import type { CsvFile } from '@/api/list'
import type { PeriodInfo } from '@/api/types'
import { minorUnitExponent } from '@/design/format'
import { addDays, resolvePeriod, type GlobalFilters } from '@/lib/filters'
import type { DemoDataset } from '@/mock/generators/engine'
import { DEMO_TIMEZONE, scopeFor } from '@/mock/generators/engine'

import { csvBlob, csvFraction, csvInstant, csvMoney, csvPlain, csvText, type MockCsvColumn } from '../list/mockCsv'

/**
 * The demo tenant's competitions list: one row per fixture competition, from the same
 * demo engine as the Overview's top competitions, so a competition's revenue and
 * tickets agree across the two. Filtered, sorted and paged by the API's rules
 * (`competition_performance/README.md`): statuses and categories compared trimmed and
 * lower-cased, nulls last in either order, ties by name then id. The advertising
 * columns are null, `needs_ad_platform`, as in production; one competition in seven
 * has a refund that cannot be placed, so the demo shows a net revenue "Not live yet"
 * cell too.
 */

const DEFAULT_LIMIT = 50

const ADVERTISING_NOT_AVAILABLE = {
  adSpendMinor: 'needs_ad_platform',
  attributedRevenueMinor: 'needs_ad_platform',
  cpaMinor: 'needs_ad_platform',
  roas: 'needs_ad_platform',
} as const

/** `sort` → the row field it orders by (`definitions.py` `SORT_FIELDS`). */
const SORT_FIELDS: Record<CompetitionSort, keyof CompetitionRow> = {
  revenue: 'revenueGrossMinor',
  revenue_net: 'revenueNetMinor',
  orders: 'orders',
  buyers: 'buyers',
  aov: 'aovMinor',
  tickets_sold_in_range: 'ticketsSoldInRange',
  tickets_sold_lifetime: 'ticketsSoldLifetime',
  total_ticket_capacity: 'totalTicketCapacity',
  sell_through: 'sellThrough',
  new_customers: 'newCustomers',
  returning_customers: 'returningCustomers',
  views: 'views',
  unique_viewers: 'uniqueViewers',
  selector_opens: 'selectorOpens',
  tickets_selected: 'ticketsSelected',
  add_to_baskets: 'addToBaskets',
  conversion_rate: 'conversionRate',
  name: 'name',
  end_at: 'endAt',
}

function normalised(value: string): string {
  return value.trim().toLowerCase()
}

function ratio(value: number): number {
  return Math.round(value * 10_000) / 10_000
}

function compareNamed(a: CompetitionRow, b: CompetitionRow): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase()) || a.competitionId.localeCompare(b.competitionId)
}

function sortRows(rows: CompetitionRow[], sort: CompetitionSort, order: 'asc' | 'desc'): CompetitionRow[] {
  const field = SORT_FIELDS[sort]
  const direction = order === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    const left = a[field]
    const right = b[field]
    if (left === null && right === null) return compareNamed(a, b)
    if (left === null) return 1
    if (right === null) return -1
    const byValue =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).toLowerCase().localeCompare(String(right).toLowerCase())
    return direction * byValue || compareNamed(a, b)
  })
}

export class MockCompetitionsStore {
  constructor(
    private readonly dataset: DemoDataset,
    private readonly currency: string,
  ) {}

  /** Every competition in the query's scope, before the status and category filters. */
  private inScope(query: CompetitionsQuery): { rows: CompetitionRow[]; period: PeriodInfo } {
    const { dataset, currency } = this
    const { tenant } = dataset
    const resolved = resolvePeriod(query.filters, dataset.now)
    // The list has no competition filter (the API's has none): every row keeps its figures.
    const dimensions = { ...query.filters.dimensions, competition: undefined }
    const scope = scopeFor(tenant, { ...query.filters, dimensions })
    const totals = dataset.period(resolved.current, scope)
    const website =
      query.filters.dimensions.website ??
      tenant.websites.find((candidate) => candidate.environment === 'production')?.id ??
      tenant.websites[0]?.id ??
      'web_demo'
    const unit = 10 ** minorUnitExponent(currency)

    const rows = tenant.competitions.map((competition, index): CompetitionRow => {
      const sold = totals.competitions.get(competition.id) ?? { tickets: 0, revenue: 0, views: 0, orders: 0 }
      const status = dataset.competitionStatus(competition)
      const word =
        status.opens > dataset.today
          ? 'scheduled'
          : status.closes < dataset.today
            ? 'drawn'
            : status.soldToDate >= competition.maxEntries
              ? 'closed'
              : 'live'
      const revenueGrossMinor = Math.round(sold.revenue * unit)
      const buyers = Math.round(sold.orders * 0.88)
      const newCustomers = Math.round(buyers * tenant.commerce.newCustomerShare)
      const views = Math.round(sold.views)
      const uniqueViewers = Math.round(views * 0.64)
      return {
        competitionId: competition.id,
        externalCompetitionId: String(1000 + index),
        websiteId: website,
        name: competition.name,
        status: word,
        active: ACTIVE_STATUSES.includes(word),
        category: competition.category,
        imageUrl: null,
        productUrl: null,
        ticketPriceMinor: Math.round(competition.ticketPrice * unit),
        ticketPriceCurrency: currency,
        startAt: `${status.opens}T09:00:00Z`,
        endAt: `${status.closes}T21:00:00Z`,
        drawAt: `${addDays(status.closes, 1)}T19:00:00Z`,
        totalTicketCapacity: competition.maxEntries,
        revenueGrossMinor,
        revenueNetMinor: index % 7 === 3 && revenueGrossMinor > 0 ? null : revenueGrossMinor - Math.round(revenueGrossMinor * 0.02),
        orders: sold.orders,
        buyers,
        aovMinor: sold.orders > 0 ? Math.round(revenueGrossMinor / sold.orders) : null,
        ticketsSoldInRange: sold.tickets,
        ticketsSoldLifetime: status.soldToDate,
        sellThrough: ratio(status.sellThrough),
        newCustomers,
        returningCustomers: buyers - newCustomers,
        views,
        uniqueViewers,
        selectorOpens: Math.round(views * 0.31),
        ticketsSelected: Math.round(sold.tickets * 1.35),
        addToBaskets: Math.round(sold.orders * 1.5),
        conversionRate: uniqueViewers > 0 ? ratio(Math.min(1, sold.orders / uniqueViewers)) : null,
        adSpendMinor: null,
        attributedRevenueMinor: null,
        cpaMinor: null,
        roas: null,
      }
    })
    const period: PeriodInfo = {
      from: resolved.current.from,
      to: resolved.current.to,
      label: resolved.label,
      compareLabel: null,
      previousFrom: null,
      previousTo: null,
    }
    return { rows, period }
  }

  /** One competition's row as the list shows it for `filters`, or none when it is not in their scope. */
  find(filters: GlobalFilters, competitionId: string): { row: CompetitionRow; period: PeriodInfo } | null {
    const { rows, period } = this.inScope({ filters })
    const row = rows.find((candidate) => candidate.competitionId === competitionId)
    return row ? { row, period } : null
  }

  /** The matching rows, sorted, with the status counts and the period. */
  private matching(query: CompetitionsQuery) {
    const { rows, period } = this.inScope(query)
    const categories = new Set((query.categories ?? []).map(normalised))
    const statuses = new Set((query.statuses ?? []).map(normalised))
    const inCategory = rows.filter((row) => categories.size === 0 || (row.category !== null && categories.has(normalised(row.category))))
    const counts = new Map<string, CompetitionStatusCount>()
    for (const row of inCategory) {
      const status = normalised(row.status)
      const entry = counts.get(status) ?? { status, count: 0, active: row.active }
      entry.count += 1
      counts.set(status, entry)
    }
    const byStatus = [...counts.values()].sort((a, b) => b.count - a.count || a.status.localeCompare(b.status))
    const filtered = inCategory.filter((row) => statuses.size === 0 || statuses.has(normalised(row.status)))
    const sort = query.sort ?? DEFAULT_COMPETITION_SORT
    const order = query.order ?? DEFAULT_SORT_ORDER
    return { rows: sortRows(filtered, sort, order), byStatus, sort, order, period }
  }

  list(query: CompetitionsQuery): { data: CompetitionsList; period: PeriodInfo } {
    const { rows, byStatus, sort, order, period } = this.matching(query)
    const limit = query.limit ?? DEFAULT_LIMIT
    const offset = query.offset ?? 0
    const page = rows.slice(offset, offset + limit)
    const notAvailable: CompetitionsList['notAvailable'] = { ...ADVERTISING_NOT_AVAILABLE }
    if (page.some((row) => row.revenueNetMinor === null)) notAvailable.revenueNetMinor = 'refund_not_itemised'
    return {
      period,
      data: {
        rows: page,
        total: rows.length,
        byStatus,
        sort,
        order,
        limit,
        offset,
        notAvailable,
        coverage: {
          firstEventAt: `${period.from}T00:00:00Z`,
          measuredFrom: `${period.from}T00:00:00Z`,
          note: 'Views, selector opens, tickets selected, add-to-baskets and conversion are counted from measured sessions only: visitors who allowed analytics. Demo data.',
        },
        currency: this.currency,
        timezone: DEMO_TIMEZONE,
      },
    }
  }

  /** The README's CSV: every matching row (up to 5,000), money in major units, with the route's headers. */
  csv(query: CompetitionsQuery): CsvFile {
    const { rows, period } = this.matching(query)
    const columns = csvColumns(this.currency)
    return {
      blob: csvBlob(columns, rows.slice(0, CSV_MAX_ROWS)),
      filename: competitionsCsvFilename(period.from, period.to),
      truncated: rows.length > CSV_MAX_ROWS,
      totalRows: rows.length,
    }
  }
}

// --- the CSV, as the API writes it -------------------------------------------

type CsvColumn = MockCsvColumn<CompetitionRow>

/**
 * The README's columns, in order, each with its cell: one list, so the header line and
 * every row are built from the same entries and cannot drift apart.
 */
function csvColumns(currency: string): readonly CsvColumn[] {
  return [
    ['competition_id', (row) => csvText(row.competitionId)],
    ['external_competition_id', (row) => csvText(row.externalCompetitionId)],
    ['website_id', (row) => csvText(row.websiteId)],
    ['name', (row) => csvText(row.name)],
    ['status', (row) => csvText(row.status)],
    ['active', (row) => csvPlain(row.active)],
    ['category', (row) => csvText(row.category)],
    ['start_at', (row) => csvInstant(row.startAt)],
    ['end_at', (row) => csvInstant(row.endAt)],
    ['draw_at', (row) => csvInstant(row.drawAt)],
    ['ticket_price', (row) => csvMoney(row.ticketPriceMinor, row.ticketPriceCurrency)],
    ['ticket_price_currency', (row) => csvText(row.ticketPriceCurrency)],
    ['total_ticket_capacity', (row) => csvPlain(row.totalTicketCapacity)],
    ['currency', () => csvText(currency)],
    ['revenue_gross', (row) => csvMoney(row.revenueGrossMinor, currency)],
    ['revenue_net', (row) => csvMoney(row.revenueNetMinor, currency)],
    ['orders', (row) => csvPlain(row.orders)],
    ['buyers', (row) => csvPlain(row.buyers)],
    ['aov', (row) => csvMoney(row.aovMinor, currency)],
    ['tickets_sold_in_range', (row) => csvPlain(row.ticketsSoldInRange)],
    ['tickets_sold_lifetime', (row) => csvPlain(row.ticketsSoldLifetime)],
    ['sell_through', (row) => csvFraction(row.sellThrough)],
    ['new_customers', (row) => csvPlain(row.newCustomers)],
    ['returning_customers', (row) => csvPlain(row.returningCustomers)],
    ['views', (row) => csvPlain(row.views)],
    ['unique_viewers', (row) => csvPlain(row.uniqueViewers)],
    ['selector_opens', (row) => csvPlain(row.selectorOpens)],
    ['tickets_selected', (row) => csvPlain(row.ticketsSelected)],
    ['add_to_baskets', (row) => csvPlain(row.addToBaskets)],
    ['conversion_rate', (row) => csvFraction(row.conversionRate)],
    ['ad_spend', (row) => csvMoney(row.adSpendMinor, currency)],
    ['attributed_revenue', (row) => csvMoney(row.attributedRevenueMinor, currency)],
    ['cpa', (row) => csvMoney(row.cpaMinor, currency)],
    ['roas', (row) => csvFraction(row.roas)],
    ['image_url', (row) => csvText(row.imageUrl)],
    ['product_url', (row) => csvText(row.productUrl)],
  ]
}
